import { ChangeEvent, useEffect, useState } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  addUserAccreditation,
  deleteUserAccreditation,
  viewUserAccreditation,
} from "@/dispatchers/memberDispatchers";

// #region Interfaces
interface Accreditation {
  id: string;
  name: string;
  type: number;
  issueDate: string;
  expiryDate: string | null;
  fileName: string;
  fileSize: string;
  fileType: string;
  document: string;
}
// #endregion Interfaces

export const useAccreditionsProfileData = () => {
  const documentTypes = [
    { label: "Professional Registration", value: 0 },
    { label: "Continuing Education Certificate", value: 1 },
    { label: "Specialty Certification", value: 2 },
    { label: "Training Certificate", value: 3 },
    { label: "License", value: 4 },
    { label: "Insurance Certificate", value: 5 },
    { label: "Other", value: 6 },
  ];

  const { appState, memberState } = useTitanApp();
  const { darkMode } = appState || {};
  const { isCurrentProfileLoading, userProfiles } = memberState || {};

  const isDarkMode = darkMode ?? false;
  const [accreditations, setAccreditations] = useState<Accreditation[]>([]);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerDocument, setViewerDocument] = useState<Accreditation | null>(
    null,
  );

  // ✅ Convert File → Base64
  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  // ✅ Populate accreditations when `userProfiles` changes
  useEffect(() => {
    if (userProfiles?.accreditations?.length) {
      const loadedAccreditations = userProfiles?.accreditations.map(
        (acc: any) => ({
          id: acc._id || Date.now().toString(),
          name: acc.name || "",
          type: acc.documentTypes ?? 6,
          issueDate: acc.issueDate
            ? new Date(acc.issueDate).toISOString().split("T")[0]
            : "",
          expiryDate: acc.expiryDate
            ? new Date(acc.expiryDate).toISOString().split("T")[0]
            : null,
          fileName: acc.fileName || "",
          fileSize: "",
          fileType: acc.fileType || "",
          document: acc.document || "",
        }),
      );
      setAccreditations(loadedAccreditations);
    }
  }, [userProfiles]);

  // ✅ Download file from Base64
  const handleFileDownload = async (accreditationView: any) => {
    try {
      const response = await viewUserAccreditation(accreditationView.id);
      const accreditation = response?.accreditations.find(
        (acc: any) => acc._id === accreditationView.id,
      );

      const byteCharacters = atob(
        accreditation ? accreditation.document : accreditationView.document,
      );
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: accreditation
          ? accreditation.fileType
          : accreditationView.fileType || "application/octet-stream",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = accreditation
        ? accreditation.fileName
        : accreditationView.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    } catch (error) {
      console.error("Error downloading accreditation:", error);
    }
  };

  const handleFileView = async (accreditationView: any) => {
    try {
      const response = await viewUserAccreditation(accreditationView.id);
      const accreditation = response?.accreditations.find(
        (acc: any) => acc._id === accreditationView.id,
      );

      setViewerDocument(accreditation ? accreditation : accreditationView);
      setIsViewerOpen(true);
    } catch (error) {
      console.error("Error viewing accreditation:", error);
    }
  };

  // ✅ Handle new file upload
  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      const base64 = await readFileAsBase64(file);

      const newAccreditation: Accreditation = {
        id: Date.now().toString(),
        name: file.name,
        type: 6, // Default to "Other"
        issueDate: new Date().toISOString().split("T")[0],
        expiryDate: null,
        fileName: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        fileType: file.type || "",
        document: base64,
      };

      setAccreditations((prev) => [...prev, newAccreditation]);
    }
  };

  // ✅ Remove accreditation
  const removeAccreditation = async (id: string) => {
    try {
      setAccreditations((prev) => prev.filter((acc) => acc.id !== id));

      const updatedList = accreditations.filter((acc) => acc.id !== id);
      const cleanedAccreditations = updatedList.map((acc) => ({
        ...acc,
        expiryDate: acc.expiryDate === "" ? null : acc.expiryDate,
      }));

      await deleteUserAccreditation(id);
    } catch (error) {
      console.error("Error deleting accreditation:", error);
    }
  };

  const updateAccreditation = (
    id: string,
    field: keyof Accreditation,
    value: string,
  ) => {
    setAccreditations((prev) =>
      prev.map((acc) =>
        acc.id === id
          ? { ...acc, [field]: field === "type" ? Number(value) : value }
          : acc,
      ),
    );
  };

  // ✅ Expiry checks
  const isExpiringWithin30Days = (expiryDate: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays >= 0;
  };

  const isExpired = (expiryDate: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  // ✅ Submit to API
  const addUserAccreditationData = async () => {
    try {
      const cleanedAccreditations = accreditations.map((acc) => ({
        ...acc,
        expiryDate: acc.expiryDate === "" ? null : acc.expiryDate,
      }));
      const data = {
        accreditations: cleanedAccreditations,
        email: userProfiles?.response?.email ?? "", // ✅ use actual email
      };

      const response = await addUserAccreditation(data);
      return response.data;
    } catch (error) {
      console.error("Error saving accreditations:", error);
      throw error;
    }
  };

  // #endregion

  return {
    isDarkMode,
    documentTypes,
    accreditations,
    updateAccreditation,
    handleFileUpload,
    isExpired,
    isExpiringWithin30Days,
    removeAccreditation,
    addUserAccreditationData,
    isCurrentProfileLoading,
    handleFileDownload,
    handleFileView,
    isViewerOpen,
    viewerDocument,
    setIsViewerOpen,
  };
};
